import { Dispatch } from 'react-redux';

import { setOpenPopup } from 'components/InvitePopups/SuccessBillingForAddingEmployeesPopup';
import { getAnyActiveMaximalRateName } from 'selectors/app.selectors';
import {
    getCheckoutArchiveRates,
    getCheckoutRatesArchive,
} from 'selectors/checkout.selectors';
import { t } from 'ttag';

import { AccountRate } from '../../services/enums';

import notificationActionCreators from '../notificationCenter/notificationCenterActionCreators';
import actions from './checkoutActions';

import { runIntervalFetchingBillStatus } from './helpers';

import { amountValueToPayloadValue } from '../../lib/numbers';
import eventTracking from '../../services/analytics/eventTracking';
import {
    createBill,
    getRatesForPayment,
    getRecommendedWebRateForPayment,
} from '../../services/billing';
import { INTEGRATION_RATES_SET } from '../../services/billing/constants';
import { generateCardPaymentPageUrl } from '../BillGenerationForm/api';
import { MAX_AMOUNT_VALUE, MIN_AMOUNT_VALUE, PAYMENT_FLOW } from './constants';

export const getRates = () => {
    return async (dispatch: Dispatch) => {
        try {
            const rates = await getRatesForPayment();

            dispatch({
                type: actions.CHECKOUT__SET_RATES,
                rates: rates,
            });
        } catch (error) {
            dispatch(
                notificationActionCreators.addNotification({
                    type: 'text',
                    textType: 'error',
                    text: `${t`Сталася помилка, спробуйте пізніше`}: ${
                        error.message
                    }`,
                    showCloseButton: true,
                    autoClose: 5000,
                }),
            );
        }
    };
};

export const getRecommendedWebRateActionCreator = () => {
    return async (dispatch: Dispatch) => {
        try {
            const recommendedWebRate = await getRecommendedWebRateForPayment();

            if (!recommendedWebRate) {
                return;
            }

            dispatch({
                type: actions.CHECKOUT__SET_RECOMMENDED_RATE,
                recommendedWebRate: recommendedWebRate.rate,
            });
        } catch (error) {
            dispatch(
                notificationActionCreators.addNotification({
                    type: 'text',
                    textType: 'error',
                    text: `${t`Сталася помилка, спробуйте пізніше`}: ${
                        error.message
                    }`,
                    showCloseButton: true,
                    autoClose: 5000,
                }),
            );
        }
    };
};

export const setChosenRate = (chosenRateName: AccountRate) => {
    return (dispatch: Dispatch, getState) => {
        const {
            checkout: { rates },
        } = getState();

        const activeRateUltimateName = getAnyActiveMaximalRateName(getState());

        const isAddEmployees = chosenRateName === AccountRate.ADD_EMPLOYEE;

        if (!rates) {
            return;
        }

        const ultimateRate = rates.rates_web.find(
            (rate) => rate.rate === activeRateUltimateName,
        );

        const chosenRate =
            chosenRateName === AccountRate.FILL_INTEGRATION
                ? AccountRate.INTEGRATION
                : chosenRateName;

        const chosenRateInformation = isAddEmployees
            ? ultimateRate.extensions?.find(({ type }) => type === 'employees')
            : rates.rates_web.find((rate) => rate.rate === chosenRate) ||
              rates.rates_integration.find(
                  (rate) => rate.rate === chosenRate,
              ) ||
              rates.rates_archive.find((rate) => rate.rate === chosenRate);

        dispatch({
            type: actions.CHECKOUT__SET_CHOSEN_RATE,
            chosenRate: chosenRateName,
            chosenRateInformation,
        });
    };
};

const getIsDocumentPayment = (chosenRate: string) =>
    chosenRate === AccountRate.FILL_INTEGRATION;

const redirectToEVOPAY = async ({
    number,
    bill_id,
    chosenRate,
    price,
}: {
    number: string | number;
    bill_id: string;
    chosenRate: AccountRate;
    price: number;
}) => {
    const description = `Оплата послуг згідно рахунку №${number} у т.ч. ПДВ`;
    const payload = {
        bill_id,
        bill_type: getIsDocumentPayment(chosenRate)
            ? 'document_payment'
            : 'rate_payment',
        price: amountValueToPayloadValue(price),
        currency: 'UAH',
        description,
    };
    const url = await generateCardPaymentPageUrl(payload);
    window.open(url, '_self');
};

const getIntegrationRate = (rate: AccountRate, amount: number) => {
    if (
        !INTEGRATION_RATES_SET.has(rate) ||
        rate === AccountRate.FILL_INTEGRATION
    ) {
        return null;
    }

    if (amount) {
        return AccountRate.INTEGRATION_WITH_DOCUMENTS;
    }

    return AccountRate.INTEGRATION;
};

export const submitCheckout = () => {
    return async (dispatch: Dispatch, getState) => {
        const {
            app: {
                currentUser: {
                    email,
                    currentCompany: { name, edrpou },
                    currentRole: { id: roleId },
                },
            },
            checkout: {
                paymentFlow,
                chosenRate,
                amount,
                price,
                selectedArchiveExtendInformation,
                amountEmployee,
                extendPrice,
            },
        } = getState();
        const isBillGeneration = paymentFlow === PAYMENT_FLOW.BILL_GENERATION;
        const isCardPayment = paymentFlow === PAYMENT_FLOW.CARD_PAYMENT;
        const integrationRate = getIntegrationRate(chosenRate, amount);
        const rate = integrationRate || chosenRate;

        if (
            amount &&
            (amount < MIN_AMOUNT_VALUE || amount > MAX_AMOUNT_VALUE)
        ) {
            dispatch({
                type: actions.CHECKOUT__SET_IS_AMOUNT_ERROR,
                isAmountError: true,
            });
            return;
        }

        dispatch({ type: actions.CHECKOUT__SET_IS_LOADING, isLoading: true });

        const isDocumentPayment = rate === AccountRate.FILL_INTEGRATION;

        try {
            const resp = await createBill({
                email: email.trim(),
                edrpou: edrpou.trim(),
                ...(amount > 0 && { count_documents: amount }),
                name: name.trim(),
                agreement: null,
                rate: isDocumentPayment ? null : rate,
                is_card_payment: isCardPayment,
                ...(amountEmployee > 0 && { employee_count: amountEmployee }),
                selectedArchiveExtend: selectedArchiveExtendInformation?.rate,
            });

            if (resp.status === 201) {
                const bill = await resp.json();
                eventTracking.sendToGTM({
                    event: 'purchase_tariff',
                    category: bill.number,
                });
                if (isBillGeneration) {
                    const callback = () => {
                        dispatch({
                            type: actions.CHECKOUT__SET_IS_LOADING,
                            isLoading: false,
                        });
                        if (
                            amountEmployee > 0 &&
                            bill.extension_status === 'active_trial'
                        ) {
                            setOpenPopup();
                        }
                    };
                    runIntervalFetchingBillStatus(
                        roleId,
                        bill.bill_id,
                        callback,
                    );
                    eventTracking.sendToGTM({
                        category: 'tariffs_issue_bill',
                        action: `${email}`,
                        label: `${rate}`,
                    });
                }
                if (isCardPayment) {
                    redirectToEVOPAY({
                        ...bill,
                        chosenRate: rate,
                        price: price + extendPrice,
                    });
                    dispatch({
                        type: actions.CHECKOUT__SET_IS_LOADING,
                        isLoading: false,
                    });
                    eventTracking.sendToGTM({
                        category: 'tariffs_card_pay',
                        action: `${email}`,
                        label: `${rate}`,
                    });
                }
            } else {
                dispatch(
                    notificationActionCreators.addNotification({
                        type: 'text',
                        textType: 'error',
                        text: t`Сталася помилка, спробуйте пізніше.`,
                        showCloseButton: true,
                        autoClose: 5000,
                    }),
                );
                dispatch({
                    type: actions.CHECKOUT__SET_IS_LOADING,
                    isLoading: false,
                });
            }
        } catch (error) {
            dispatch(
                notificationActionCreators.addNotification({
                    type: 'text',
                    textType: 'error',
                    text: `${t`Сталася помилка, спробуйте пізніше`}: ${
                        error.message
                    }`,
                    showCloseButton: true,
                    autoClose: 5000,
                }),
            );
            dispatch({
                type: actions.CHECKOUT__SET_IS_LOADING,
                isLoading: false,
            });
        }
    };
};

export const setIsActiveArchiveExtend = (isActiveArchiveExtend: boolean) => ({
    type: actions.CHECKOUT__SET_IS_ACTIVE_ARCHIVE_EXTEND,
    payload: isActiveArchiveExtend,
});

export const setSelectedArchiveExtend = (selectedArchiveExtend: string) => (
    dispatch: Dispatch,
    getState,
) => {
    if (!selectedArchiveExtend) {
        dispatch({
            type: actions.CHECKOUT__SET_SELECTED_ARCHIVE_EXTEND,
            payload: {
                selectedArchiveExtend: selectedArchiveExtend,
                selectedArchiveExtendInformation: null,
                extendPrice: 0,
            },
        });
    }
    const state = getState();
    const archiveRates = getCheckoutArchiveRates(state)!;

    const selectedArchiveExtendInformation = archiveRates.find(
        (archiveRate) => archiveRate.rate === selectedArchiveExtend,
    );

    dispatch({
        type: actions.CHECKOUT__SET_SELECTED_ARCHIVE_EXTEND,
        payload: {
            selectedArchiveExtend: selectedArchiveExtend,
            selectedArchiveExtendInformation: selectedArchiveExtendInformation,
            extendPrice: selectedArchiveExtendInformation?.price_per_year || 0,
        },
    });
};

export default {
    getRates,
    getRecommendedWebRateActionCreator,
    setChosenRate,
    submitCheckout,
};
